import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import {useForm} from 'react-hook-form';
import ReactModal from "react-modal";
import {userApi} from "../../../../apis/admin/user/userApi";
import {useSystemMessage} from "../../../../hooks/useSystemMessage";
import {PasswordDisplayButton} from "../../common/PasswordDisplayButton";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {ApiClientError} from "../../../../apis/errors";
import useAppRouter from "../../../../hooks/useAppRouter";
import {logError} from "../../../../libs/utils/logError";
import useLoading from "../../../../hooks/useLoading";

interface PasswordEditModalProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSaved: () => Promise<void>;
}

export interface PasswordEditFormValues {
  password: string;
  confirmPassword: string;
}

export const PasswordEditModal = (props: PasswordEditModalProps) => {
  const { setShowModal, onSaved} = props;
  const { success } = useSystemMessage();
  const [ abortController, setAbortController ] = useState<AbortController | undefined>();
  const [ isShowPassword, setIsShowPassword ] = useState<boolean>(false);
  const [ isShowConfirmPassword, setIsShowConfirmPassword ] = useState<boolean>(false);
  const [ apiClientError, setApiClientError] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();
  const withLoading = useLoading();
  const location = useLocation();
  const defaultValues: PasswordEditFormValues = {
    password: '',
    confirmPassword: '',
  };
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    reValidateMode: 'onSubmit',
  });
  const password = watch('password');

  const handleSave = async (): Promise<void> => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: PasswordEditFormValues) => {
        try {
          setAbortController(new AbortController());
          await userApi.updatePassword(
            {
              password: data.password,
            },
            {abortSignal: abortController?.signal});
          setAbortController(undefined);
          await success('パスワードを変更しました。');
          await onSaved();

        } catch (e) {
          await logError(
            location.pathname,
            'パスワードの変更に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="form-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">パスワード変更</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
          </div>
          <form>
            <div className="form-modal-content">
              <div className="w-full">
                <div className="mb-4 text-danger text-xs">
                  { apiClientError?.message }
                  { (Object.keys(errors).length > 0) && '入力エラーがあります' }
                </div>

                { /* パスワード */ }
                <div className="mb-4">
                  <label htmlFor="password">
                    <div className="text-xs font-semibold text-primary mb-1">
                      <span>パスワード</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <div className="relative">
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register(
                          "password",
                          makeRegisterOptions('パスワード', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 64 },
                            { type: FormValidationType.PASSWORD, typeName: 'password' },
                          ])
                        )}
                        id="password"
                        type={ isShowPassword ? 'text' : 'password' }
                        className="text-field"
                        maxLength={64}
                      />
                      <div
                        className="absolute inset-y-0 right-0 pr-3 text-gray-400 flex items-center text-sm leading-5 focus:outline-none"
                      >
                        <PasswordDisplayButton
                          {...{
                            isShow: isShowPassword,
                            setIsShow: setIsShowPassword,
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-1 text-red-500 text-xs">
                      {errors.password?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'password',
                      }}
                    />
                  </label>
                </div>

                { /* パスワード（確認） */ }
                <div className="mb-4">
                  <label htmlFor="confirmPassword">
                    <div className="text-xs font-semibold text-primary mb-1">
                      <span>パスワード（確認）</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <div className="relative">
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register(
                          "confirmPassword",
                          makeRegisterOptions('パスワード（確認）', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 64 },
                            { type: FormValidationType.PASSWORD, typeName: 'password' },
                            {
                              type: FormValidationType.CONFIRMED,
                              otherFieldName: 'パスワード',
                              otherFieldValue: password,
                              typeName: 'confirmedPassword',
                            },
                          ])
                        )}
                        id="confirmPassword"
                        type={ isShowConfirmPassword ? 'text' : 'password' }
                        className="text-field"
                        maxLength={64}
                      />
                      <div
                        className="absolute inset-y-0 right-0 pr-3 text-gray-400 flex items-center text-sm leading-5 focus:outline-none"
                      >
                        <PasswordDisplayButton
                          {...{
                            isShow: isShowConfirmPassword,
                            setIsShow: setIsShowConfirmPassword,
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-1 text-red-500 text-xs">
                      {errors.confirmPassword?.message}
                    </div>
                  </label>
                </div>

              </div>
              <div className="form-modal-footer-container">
                <div className="form-modal-footer">
                  <button
                    className="btn-sub"
                    type="button"
                    onClick={() => setShowModal(false)}
                  >
                    キャンセル
                  </button>
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={handleSave}
                  >
                    保 存
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};